import React, { useEffect, useState } from 'react';
import { ReactComponent as ReassignPartnerIcon } from 'assets/icons/time-elapse.svg';
import { ReactComponent as MotorBikeIcon } from 'assets/icons/motorbike.svg';
import { ReactComponent as RightArrow } from 'assets/icons/chevron-right.svg';
import {
  BaseModal,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { OrderType } from 'models/order.model';
import { apiService } from 'utils/api';
import { handleApiError } from 'utils/functions';
import { DeliveryPartnerConfigType } from 'models/polygon.model';
import { toast } from 'react-toastify';

type Props = {
  show: boolean;
  onClose: () => void;
  order: OrderType;
};

type ReassignDeliveryPartnerFooterProps = {
  loading: boolean;
  onConfirm: () => void;
  step: number;
  changeStep: (val: number) => void;
};

const ReassignPartnerFooter = ({
  onConfirm,
  loading,
  changeStep,
  step
}: ReassignDeliveryPartnerFooterProps) => (
  <div className='flex justify-between items-center gap-4'>
    {step === 1 && (
      <Button
        small
        color='primary'
        variant='block'
        className='mx-auto '
        loading={loading}
        onClick={() => changeStep(2)}>
        Confirm
      </Button>
    )}
    {step === 2 && (
      <>
        <Button
          small
          color='gray'
          variant='outline'
          className='mx-auto'
          disabled={loading}
          onClick={() => changeStep(1)}>
          Cancel
        </Button>
        <Button
          small
          color='primary'
          variant='block'
          className='mx-auto'
          loading={loading}
          onClick={onConfirm}>
          Confirm
        </Button>
      </>
    )}
  </div>
);

const ReassignDeliveryPartnerModal = ({ show, onClose, order }: Props) => {
  const polygonId = order?.calculated_order?.cokitchen_polygon_id;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deliveryPartners, setDeliveryPartners] = useState(['']);
  const [formDetails, setFormDetails] = useState({
    selectedPartner: ''
  });

  const getDeliveryPartner = async () => {
    try {
      const res = await apiService.get('logistics-configs', {
        params: { cokitchen_polygon_id: polygonId },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });
      if (res.data.data.length > 0) {
        const partnersInDB = res.data.data as DeliveryPartnerConfigType[];
        setDeliveryPartners(partnersInDB[0].config.map((config) => config.logistic_company_id));
      }
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleConfirm = async () => {
    if (!formDetails.selectedPartner) return;
    setLoading(true);
    try {
      await apiService.patch(`order-routing/${order.id}/delivery-partner`, {
        data: { delivery_partner: formDetails.selectedPartner },
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
      });

      toast(<ToastMessage message={'Partner Successfully changed'} type='success' />);
      onClose();
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDeliveryPartner();
  }, []);

  return (
    <BaseModal
      isOpen={show}
      title={step === 1 ? `Reassign Delivery Partner` : 'Confirm partner'}
      description={step === 1 ? 'Set order reassignment for this order' : ''}
      titleIcon={<ReassignPartnerIcon />}
      titleIconColor='success'
      onRequestClose={onClose}
      footer={
        <ReassignPartnerFooter
          step={step}
          changeStep={(val) => setStep(val)}
          loading={loading}
          onConfirm={handleConfirm}
        />
      }>
      {step === 1 && (
        <Dropdown
          className='left-0 relative'
          content={
            <div className='border-l-4 border-transparent border-t border-t-[#ebebeb] flex py-4 items-center cursor-pointer transition-all hover:border-l-red-5 hover:bg-[#F4F5F5] px-1'>
              <MotorBikeIcon className='text-red-5' />
              <p className='grow pl-4 text-sm'>
                {formDetails.selectedPartner ? (
                  <span className='capitalize text-xs font-medium text-gray-6 bg-gray-1/60 rounded px-2 p-1'>
                    {formDetails.selectedPartner.replaceAll('_', ' ')}
                  </span>
                ) : (
                  <span className='text-gray-6 font-medium'>Select delivery partner</span>
                )}
              </p>

              <RightArrow />
            </div>
          }>
          <DropdownList className='min-w-[400px] bg-transparent shadow-none'>
            <div className='bg-white w-fit shadow border-gray-1 border rounded-lg overflow-auto max-h-60'>
              {deliveryPartners.map((partner) => (
                <DropdownItem key={partner} className='list-none w-full'>
                  <Input
                    className='text-sm transition-all duration-200 capitalize'
                    type='radio'
                    name='reassign-partner'
                    label={partner.replaceAll('_', ' ')}
                    value={partner}
                    checked={formDetails.selectedPartner === partner}
                    onChange={(e) =>
                      setFormDetails({ ...formDetails, selectedPartner: e.target.value })
                    }
                  />
                </DropdownItem>
              ))}
            </div>
          </DropdownList>
        </Dropdown>
      )}
      {step === 2 && (
        <div>
          <p className=''>
            Are you sure you want to reassign order {order.order_code} from{' '}
            <span className='font-medium text-red-5 capitalize'>
              {order?.order_details?.delivery_handled_by ?? 'Foodcourt'}
            </span>{' '}
            to{' '}
            <span className='font-medium text-red-5 capitalize'>{formDetails.selectedPartner}</span>
          </p>
        </div>
      )}
    </BaseModal>
  );
};

export default ReassignDeliveryPartnerModal;
